import React, { useState, useEffect, useRef } from 'react';
import {
  TableCell,
  TableRow,
  Button,
  Chip,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { getUserSubscriptionForAdmin } from '../../service/service';
import { formatDate } from '../../utils/helperFunction';
import { Howl } from 'howler';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';
import StarIcon from '@mui/icons-material/Star';

const AIFeedbackRow = ({ feedback, currentPlayingId, setCurrentPlayingId, onExpertReviewChange }) => {
  const [audioElement, setAudioElement] = useState(null);
  const ffmpegRef = useRef(new FFmpeg());  // Store ffmpeg instance in a ref
  const [transcodedUrl, setTranscodedUrl] = useState(feedback.answer);
  const [isPremium, setIsPremium] = useState(false);

  const getUserPlan = async () => {
    const response = await getUserSubscriptionForAdmin(feedback.userId._id);

    if (response.data.planName === 'Free Plan') {
      setIsPremium(false);
    } else {
      setIsPremium(true);
    }
  }

  useEffect(() => {
    getUserPlan();
  }, [isPremium]);
  
  const transcode = async (audioUrl) => {
    const ffmpeg = ffmpegRef.current;
    await ffmpeg.load();

    try {
      await ffmpeg.writeFile('input.m4a', await fetchFile(audioUrl));
      await ffmpeg.exec(['-i', 'input.m4a', 'output.mp3']);
      const data = await ffmpeg.readFile('output.mp3');
      const url = URL.createObjectURL(new Blob([data], { type: 'audio/mp3' })); // Store transcoded URL in state
      setTranscodedUrl(url);
    } catch (error) {
      console.error('Transcoding failed:', error);
      return null;
    }
  };

  // Audio playback function
  const handlePlayPause = async (audioUrl) => {
    if (currentPlayingId === feedback._id) {
      audioElement?.pause();
      setCurrentPlayingId(null);
    } else {
      if (audioElement) audioElement.stop(); // Stop any currently playing audio

      const transcodedUrl = await transcode(audioUrl); // Transcode before playback
      if (!transcodedUrl) {
        console.error('Failed to transcode audio');
        return;
      }

      const newAudio = new Howl({
        src: [transcodedUrl],
        format: ['mp3']
      });

      newAudio.play();
      setAudioElement(newAudio);
      setCurrentPlayingId(feedback._id);

      newAudio.on('end', () => {
        setCurrentPlayingId(null);
      });
    }
  };

  // Get AI Score display
  const getAIScoreDisplay = () => {
    if (feedback.aiScore !== undefined) {
      return `${feedback.aiScore.toFixed(2)}%`;
    }
    return 'N/A';
  };

  // Get AI Output summary
  const getAIOutputSummary = () => {
    if (feedback.aiOutput) {
      const target_accent = feedback.aiOutput.model_metadata?.target_accent || feedback.aiOutput.target_accent || null;

      return {
        similarity: feedback.aiOutput.accent_probability_raw,
        detectedAccent:
          "model is trying to find: " + target_accent,
        detectedConfidence: "The above is target accent of model",
        message: feedback.aiOutput.prediction,
      };
    }
    return null;
  };

  const aiOutputSummary = getAIOutputSummary();

  const renderReviewOptions = (reviewValue, fieldName) => (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <FormControlLabel
        sx={{ my: 0 }}
        label="Correct"
        control={
          <Checkbox
            size="small"
            checked={reviewValue === true}
            onChange={() => onExpertReviewChange(feedback, fieldName, reviewValue === true ? null : true)}
          />
        }
      />
      <FormControlLabel
        sx={{ my: 0 }}
        label="Incorrect"
        control={
          <Checkbox
            size="small"
            checked={reviewValue === false}
            onChange={() => onExpertReviewChange(feedback, fieldName, reviewValue === false ? null : false)}
          />
        }
      />
    </Box>
  );

  return (
    <TableRow sx={{ backgroundColor: isPremium ? 'lightyellow' : 'transparent' }}>
      <TableCell>{feedback.order}
      {isPremium && <StarIcon sx={{ color: 'gold',stroke: "#FF5100FF", strokeWidth: 1}} />}
      </TableCell>
      <TableCell>
        <div>{feedback?.userId?.email}</div>
        <div><b>Speaking - </b>{feedback?.userId?.primaryAccent}</div>
      </TableCell>
      <TableCell>{formatDate(feedback?.createdAt)}</TableCell>
      <TableCell sx={{ width: '25%' }}>{feedback.originalQuestionId.phrase}</TableCell>
      <TableCell>
        <Button
          variant="contained"
          color="primary"
          onClick={() => transcode(feedback.answer)}
        >
          Transcode
        </Button>
        <p></p>
        <audio src={transcodedUrl} controls />
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {feedback.expertAction === 'correct' ? (
            <Chip
              icon={<CheckIcon />}
              label="Correct"
              color="success"
              variant="outlined"
              size="small"
            />
          ) : (
            <Chip
              icon={<CloseIcon />}
              label="Incorrect"
              color="error"
              variant="outlined"
              size="small"
            />
          )}
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight="bold">
          {getAIScoreDisplay()}
        </Typography>
      </TableCell>
      <TableCell sx={{ width: '30%' }}>
        {aiOutputSummary ? (
          <Box>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              Similarity: {aiOutputSummary.similarity}
            </Typography>
            <Typography variant="body2" gutterBottom>
              Detected Accent: {aiOutputSummary.detectedAccent} ({((aiOutputSummary.detectedConfidence || 0) * 100).toFixed(1)}%)
            </Typography>
            <Typography variant="caption" sx={{ 
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {aiOutputSummary.message}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No AI Output Available
          </Typography>
        )}
      </TableCell>
      <TableCell>
        {renderReviewOptions(feedback.isUserSubmissionCorrectByExpert, 'isUserSubmissionCorrectByExpert')}
      </TableCell>
      <TableCell>
        {renderReviewOptions(feedback.isAIFeedbackCorrectByExpert, 'isAIFeedbackCorrectByExpert')}
      </TableCell>
    </TableRow>
  );
};

export default AIFeedbackRow;
